import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Tooltip,
  styled,
  Autocomplete,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  alpha,
  Grid,
  Card,
  Backdrop,
  Fade,
  Skeleton,
} from '@mui/material';
import {
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  Store as StoreIcon,
  Storage as DatabaseIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Layers as LayersIcon,
  Person as UserIcon,
  Login as EnterIcon,
  ExitToApp as ExitIcon,
  AccessTime as ClockIcon,
  CancelOutlined as CancelIcon,
  Map as MapIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ChevronDownIcon,
  CleaningServices as CleaningServicesIcon,
  AccountCircle as AccountCircleIcon,
  Hotel as HotelIcon,
  Check as CheckIcon,
  AttachMoney as RateIcon,
  Inventory2 as InventoryIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Apps as AppsIcon,
  RestaurantMenu as RestaurantMenuIcon,
  MapsHomeWork as HallIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ThemeToggleButton from '../common/ThemeToggleButton';
import { getHotels, switchHotel, getBookingList } from '../../services/api';
import { isNexaStayPublicHost, isNexaStayPublicPathname } from '../../nexastay/useNexaStayBasePath';
import AlertDialog from '../common/AlertDialog';
import BookingNotificationContainer from '../common/BookingNotificationContainer';
import RestaurantQrOrderNotificationDialog from '../common/RestaurantQrOrderNotificationDialog';
import { useBookingNotifications } from '../../hooks/useBookingNotifications';
import { useRestaurantQrOrderNotifications } from '../../hooks/useRestaurantQrOrderNotifications';
import { getStaticFileUrl } from '../../utils/staticUtils.js';
import {
  PERMISSIONS,
} from '../../utils/featurePermissions';
import usePermission from '../../auth/usePermission';
import { HOTEL_MODULES } from '../../auth/modules';
import { getPermissionDeniedMessage } from '../../auth/permissions';
import useMobileView from '../../hooks/useMobileView';
import { MOBILE_LAYOUT, getMobileShellViewportSx } from '../../utils/mobileView';
import MobileAppHeader from './mobile/MobileAppHeader';
import MobileHotelPickerSheet from './mobile/MobileHotelPickerSheet';
import MobileProfileMenuSheet from './mobile/MobileProfileMenuSheet';
import MobileLockScreenOverlay from './mobile/MobileLockScreenOverlay';
import ProfileDesktopMenu from '../profile/ProfileDesktopMenu';
import MobileBottomNav from './mobile/MobileBottomNav';
import MobileMoreDrawer from './mobile/MobileMoreDrawer';
import { mobileAppBackground, MOBILE_APP_HEADER_HEIGHT, MOBILE_BOTTOM_NAV_HEIGHT, MOBILE_FAB_LIFT, mobileHideScrollbarSx } from './mobile/mobileAppTheme';
import MobileShellGraphBackdrop from './mobile/MobileShellGraphBackdrop';
import { getMobileNavGroups, getMobileBottomTabs, getMobileFabAction } from '../../config/mobileAppNavigation';
import { useSyncedTheme } from '../../hooks/useSyncedTheme';
import useMobileKeyboardLayout from '../../hooks/useMobileKeyboardLayout';
import { DEFAULT_APP_THEME, getStoredThemePreference } from '../../utils/themeUtils';
import { scheduleDashboardPrefetch } from '../../utils/dashboardPrefetch';
import { dashboardNeedsRefresh, invalidateDashboardHomeCaches } from '../../utils/dashboardRefreshCoordinator';
import { THEME_COLORS as APP_THEME } from '../../shared/constants/theme';
import {
  getShellMenuItemSx,
  getShellMenuPaperSx,
  getShellMenuUserHeaderSx,
  getTopBarGridMenuPaperSx,
} from '../../utils/mobileShellMenuLayout';
import './MainLayout.css';
import moment from 'moment';

// Constants matching exact CSS values
const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 60;
const TOPBAR_HEIGHT = 60;
/** Nav tiles must stay below toolbar height or strict browsers show a vertical scrollbar on the bar. */
const TOPBAR_NAV_TILE = 52;

const DARK_CHROME_BG = APP_THEME.SURFACE_HEADER_DARK;
const DARK_CHROME_GRADIENT = `linear-gradient(180deg, ${APP_THEME.SURFACE_HEADER_DARK} 0%, ${APP_THEME.BACKGROUND_DARK} 100%)`;
const DARK_CHROME_BORDER = 'rgba(255, 255, 255, 0.08)';

// Theme colors matching CSS variables
const THEME_COLORS = {
  dark: {
    background: DARK_CHROME_BG,
    backgroundBody: APP_THEME.BACKGROUND_DARK,
    text: '#e2e8f0',
    textAdditional: 'rgba(148, 163, 184, 0.92)',
    hover: 'rgba(255, 255, 255, 0.06)',
    border: DARK_CHROME_BORDER,
    icon: 'rgba(148, 163, 184, 0.8)',
    accent: '#4ce1b6',
  },
  light: {
    background: '#ffffff',
    backgroundBody: '#f2f4f7',
    text: 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
    textAdditional: '#646777',
    hover: 'rgb(228.75, 234, 249.75)',
    border: '#eff1f5',
    icon: '#dddddd',
    accent: '#4ce1b6',
  },
};

// Icon mapping for menu items
const iconMap = {
  'lnr-home': HomeIcon,
  'lnr-calendar-full': CalendarIcon,
  'lnr-store': StoreIcon,
  'lnr-database': DatabaseIcon,
  'lnr-users': PeopleIcon,
  'lnr-cog': SettingsIcon,
  'lnr-layers': LayersIcon,
  'lnr-user': UserIcon,
  'lnr-enter': EnterIcon,
  'lnr-exit': ExitIcon,
  'lnr-clock': ClockIcon,
  'lnr-cross-circle': CancelIcon,
  'lnr-map': MapIcon,
  'lnr-home': HomeIcon, // Also used for housekeeping
  'lnr-cleaning': CleaningServicesIcon,
  'lnr-tag': LocalOfferIcon, // For pricing
  'lnr-rate': RateIcon, // For rate management
  'mui-trending-up': TrendingUpIcon,
  'mui-assessment': AssessmentIcon,
};

// Styled AppBar — same surface as left drawer (StyledDrawer .MuiDrawer-paper)
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isTopMenuOpen',
})(({ isDark, isTopMenuOpen }) => ({
  height: TOPBAR_HEIGHT,
  overflow: 'hidden',
  backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
  backgroundImage: isDark ? DARK_CHROME_GRADIENT : 'none',
  boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.04)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)',
  borderBottom: `1px solid ${isDark ? DARK_CHROME_BORDER : '#e8eaed'}`,
  zIndex: isTopMenuOpen ? 1490 : 101,
  '&.MuiAppBar-root': {
    backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
    backgroundImage: 'none',
    boxShadow: isDark ? '0 1px 30px 1px rgb(181 233 211 / 11%)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)',
  },
}));

// Styled Drawer matching exact CSS - Icon with Label App Sidebar
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'sidebarOpen' && prop !== 'isMobile',
})(({ isDark, sidebarOpen, isMobile }) => ({
  width: isMobile ? '100%' : '100px', // Fixed width for icon + label sidebar
  flexShrink: 0,
  zIndex: isMobile ? 99 : 100, // Mobile: below header, Desktop: lower than header (101)
  '& .MuiDrawer-paper': {
    width: isMobile ? '100%' : '100px', // Fixed width for icon + label sidebar
    boxSizing: 'border-box',
    background: isDark ? DARK_CHROME_GRADIENT : '#ffffff',
    backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
    paddingTop: isMobile ? '0' : `${TOPBAR_HEIGHT}px`,
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isMobile
      ? '0 2px 8px rgba(0, 0, 0, 0.1)'
      : (isDark ? 'inset -1px 0 0 rgba(255,255,255,0.04)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)'),
    borderRight: isMobile ? 'none' : (isDark ? `1px solid ${DARK_CHROME_BORDER}` : '1px solid #e8eaed'),
    position: isMobile ? 'relative' : 'fixed',
    height: isMobile ? 'calc(100vh - 60px)' : '100vh',
    top: isMobile ? '60px' : 0,
    left: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    zIndex: isMobile ? 99 : 100,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      borderRadius: '3px',
      '&:hover': {
        background: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
}));

// Styled ListItemButton matching exact CSS - Fancy Creative Button Style
const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isSelected' && prop !== 'sidebarOpen',
})(({ isDark, isSelected, sidebarOpen }) => ({
  height: 'auto',
  minHeight: '70px',
  width: 'calc(100% - 8px)',
  margin: '4px',
  padding: '12px 8px',
  position: 'relative',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  background: isSelected 
    ? (isDark 
        ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.18) 0%, rgba(76, 225, 182, 0.10) 100%)' 
        : 'linear-gradient(135deg, rgba(76, 225, 182, 0.15) 0%, rgba(76, 225, 182, 0.08) 100%)')
    : (isDark
        ? 'transparent'
        : 'rgba(0, 0, 0, 0.01)'),
  borderRadius: '12px',
  border: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderTop: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.5)' : 'rgba(76, 225, 182, 0.4)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderBottom: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'}`,
  borderLeft: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderRight: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: isSelected 
    ? (isDark 
        ? '0 4px 12px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(76, 225, 182, 0.1)' 
        : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 0 1px rgba(76, 225, 182, 0.08)')
    : (isDark
        ? 'none'
        : '0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 1px rgba(255, 255, 255, 0.9)'),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: isSelected
    ? '#4ce1b6'
    : (isDark ? '#e2e8f0' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: isSelected
      ? (isDark 
          ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.25) 0%, rgba(76, 225, 182, 0.15) 100%)' 
          : 'linear-gradient(135deg, rgba(76, 225, 182, 0.22) 0%, rgba(76, 225, 182, 0.12) 100%)')
      : (isDark 
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)' 
          : 'linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.04) 100%)'),
    transform: 'translateY(-2px)',
    boxShadow: isSelected
      ? (isDark 
          ? '0 6px 16px rgba(76, 225, 182, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(76, 225, 182, 0.15)' 
          : '0 6px 16px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 0 1px rgba(76, 225, 182, 0.12)')
      : (isDark 
          ? '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.03)' 
          : '0 3px 8px rgba(0, 0, 0, 0.12), inset 0 1px 1px rgba(255, 255, 255, 0.95)'),
    border: isSelected 
      ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.5)' : 'rgba(76, 225, 182, 0.4)'}`
      : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
    borderTop: isSelected 
      ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.6)' : 'rgba(76, 225, 182, 0.5)'}`
      : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: isSelected ? '50px' : '0px',
    width: '4px',
    background: isSelected 
      ? 'linear-gradient(180deg, rgba(76, 225, 182, 1) 0%, rgba(76, 225, 182, 0.8) 100%)'
      : 'transparent',
    borderRadius: '0 3px 3px 0',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isSelected ? '0 0 12px rgba(76, 225, 182, 0.6), inset -1px 0 2px rgba(255, 255, 255, 0.2)' : 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: isSelected 
      ? 'radial-gradient(circle at 30% center, rgba(76, 225, 182, 0.15) 0%, transparent 60%)'
      : 'transparent',
    opacity: isSelected ? 1 : 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    borderRadius: '11px',
  },
  '&:active': {
    transform: 'translateY(0px) scale(0.97)',
    boxShadow: isSelected
      ? (isDark 
          ? '0 2px 6px rgba(76, 225, 182, 0.25), inset 0 2px 4px rgba(0, 0, 0, 0.15)' 
          : '0 2px 6px rgba(76, 225, 182, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.08)')
      : (isDark 
          ? 'inset 0 3px 6px rgba(0, 0, 0, 0.25)' 
          : 'inset 0 3px 6px rgba(0, 0, 0, 0.12)'),
  },
}));

// Styled ListItemIcon matching exact CSS - Fancy Creative Style
const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ isSelected }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  minWidth: '32px',
  width: '32px',
  height: '32px',
  margin: 0,
  marginBottom: '6px',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: isSelected ? '100%' : '0%',
    height: isSelected ? '100%' : '0%',
    borderRadius: '50%',
    background: isSelected ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '26px',
    width: '26px',
    height: '26px',
    color: isSelected
      ? '#4ce1b6'
      : 'rgba(148, 163, 184, 0.85)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 1,
    filter: isSelected ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
  },
}));

// Styled ListItemText for labels below icons - Fancy Creative Style
const StyledListItemText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isSelected',
})(({ isDark, isSelected }) => ({
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: isSelected ? 700 : 500,
  margin: 0,
  padding: 0,
  color: isSelected ? '#4ce1b6' : (isDark ? 'rgba(148, 163, 184, 0.92)' : '#666'),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  textAlign: 'center',
  letterSpacing: '0.02em',
  maxWidth: '92px',
  textShadow: isSelected ? '0 1px 2px rgba(76, 225, 182, 0.2)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));


/**
 * Main Layout Component
 * Wraps all pages with header, sidebar, and footer matching the original design
 * Using Material Design components
 */
const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { isCompact: useMobileAppShell, isTabletNative, isTabletLayout } = useMobileView();
  const { keyboardOpen } = useMobileKeyboardLayout(useMobileAppShell);
  const isMobileDashboardHome =
    useMobileAppShell
    && (location.pathname === '/dashboard/' || location.pathname === '/dashboard');
  const isDesktopDashboardHome =
    !useMobileAppShell
    && (location.pathname === '/dashboard/' || location.pathname === '/dashboard');
  const isDashboardHome = isMobileDashboardHome || isDesktopDashboardHome;
  const isDashboardViewportLock = isDashboardHome && (useMobileAppShell || isLargeScreen);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const mobileNavGroups = useMemo(() => getMobileNavGroups(), [location.pathname]);
  const mobileBottomTabs = useMemo(() => getMobileBottomTabs(mobileNavGroups), [mobileNavGroups]);
  const mobileFabAction = useMemo(() => getMobileFabAction(mobileNavGroups), [mobileNavGroups]);
  const mobileLeftTabs = mobileBottomTabs.slice(0, 2);
  const mobileRightTabs = mobileBottomTabs.slice(2, 3);
  const syncedTheme = useSyncedTheme();
  const [optimisticPathname, setOptimisticPathname] = useState(null);
  const chromeThemeMode = getStoredThemePreference() ?? syncedTheme;
  const isDark = chromeThemeMode === 'dark';
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [screenLocked, setScreenLocked] = useState(false);
  const [hotelMenuAnchor, setHotelMenuAnchor] = useState(null);
  const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const isDashboardMenuOpen = Boolean(dashboardMenuAnchor);
  const isSettingsMenuOpen = Boolean(settingsMenuAnchor);
  const isTopMenuOpen = Boolean(dashboardMenuAnchor) || Boolean(settingsMenuAnchor);
  const [hotels, setHotels] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  /**
   * ROOMNEXA_MAINLAYOUT_DEFERRED_MENU_NAV — find this string to read the full story in one place.
   *
   * What: Deferred navigation for the Insights and Apps top-bar `Menu`s (MUI `Menu` + `hideBackdrop`
   * + shared blur `Backdrop` below the menu z-index).
   *
   * Bug: After choosing a route from Insights/Reports (or Apps) and then using the side menu, an
   * invisible layer could stay on top: buttons did not receive clicks; page scroll still worked.
   * A full reload fixed it until the next time navigation raced the menu close.
   *
   * Why: Calling `navigate()` in the same event as closing the menu could re-render the tree before
   * MUI’s `Menu` / `Modal` / transition had fully unmounted, leaving a stuck portal or backdrop.
   *
   * Fix:
   * - Tile click: assign `pendingInsightsNavRef` or `pendingAppsNavRef` to the target path, then
   *   only run the close handler (clear anchor). Do not call `navigate` in the click handler.
   * - `Menu` `TransitionProps.onExited`: read the ref, set it to `null`, then `navigate(url)` if a
   *   URL was pending. That runs only after the exit animation / unmount path has completed.
   * - `closeAllTopMenus`: also sets both pending refs to `null` so closing without choosing a tile
   *   does not leave a stale redirect. Same when `location.key` changes (see effect below).
   *
   * Where to look in this file: grep `pendingInsightsNavRef` (Insights `Menu`) and
   * `pendingAppsNavRef` (Apps `Menu`); each has `TransitionProps` + `onExited` and grid tile
   * `onClick`. Blur `Backdrop` is the sibling block with `zIndex: 1200` above the route `Outlet`.
   */
  const pendingInsightsNavRef = useRef(null);
  const pendingAppsNavRef = useRef(null);

  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('error');
  
  // Show alert dialog
  const showAlert = (message, title = 'Error', severity = 'error') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertDialogOpen(false);
  };

  // Get user and hotel data from Django context
  const djangoContext = window.DJANGO_CONTEXT || {};
  const user = djangoContext.user || {};
  const selectedHotel = djangoContext.selectedHotel || null;
  const permissions = usePermission();
  const canUse = (permissionCode) => permissions.can(permissionCode);
  const hasHotelFeature = (permissionCode) => permissions.isHotelFeatureAssigned(permissionCode);
  const hasHotelModule = (moduleCode) => permissions.isModuleAssigned(moduleCode);

  // Check authentication and hotel selection
  useEffect(() => {
    // NexaStay public booking site uses guest auth in NexaStayLayout, not staff login.
    if (isNexaStayPublicHost() || isNexaStayPublicPathname(location.pathname)) {
      return;
    }

    // Skip redirect if already on landing page or login pages
    const currentPath = location.pathname;
    const publicPaths = ['/onboarding/landing/', '/login/', '/password-reset/'];
    const isPublicPath = publicPaths.some(path => currentPath.startsWith(path));
    
    if (isPublicPath) {
      return; // Don't redirect if on public pages
    }

    // First check if user is authenticated
    if (!user || !user.id) {
      // User is not authenticated, redirect to login
      const nextUrl = currentPath !== '/' ? currentPath : '/onboarding/landing/';
      navigate(`/login/?next=${encodeURIComponent(nextUrl)}`, { replace: true });
      return;
    }

    // User is authenticated, check if hotel is selected
    // Backend will handle validation for hotels and organisation
    if (!selectedHotel) {
      // No hotel selected, redirect to landing page
      navigate('/onboarding/landing/', { replace: true });
    }
  }, [user, selectedHotel, navigate, location]);

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };
  const csrfToken = getCsrfToken();

  // Search bookings function
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await getBookingList({
        search: query.trim(),
        per_page: 10,
      });

      if (response && response.Data) {
        // Backend already filters with OR logic, so we can use results directly
        setSearchResults(response.Data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery && searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 500);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Restore desktop light/dark immediately when leaving mobile shell (before paint).
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    if (useMobileAppShell) {
      const userTheme = getStoredThemePreference() ?? DEFAULT_APP_THEME;
      document.body.className = `app theme-${userTheme} theme`;
      document.body.classList.add('mobile-shell-theme');
      document.documentElement.classList.add('mobile-shell-theme');
      document.documentElement.classList.toggle('theme-dark', userTheme === 'dark');
      document.documentElement.classList.toggle('theme-light', userTheme !== 'dark');
      return;
    }

    const userTheme = getStoredThemePreference() ?? DEFAULT_APP_THEME;
    document.body.className = `app theme-${userTheme} theme`;
    document.body.classList.remove('mobile-shell-theme');
    document.documentElement.classList.remove('mobile-shell-theme');
    document.documentElement.classList.toggle('theme-dark', userTheme === 'dark');
    document.documentElement.classList.toggle('theme-light', userTheme !== 'dark');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }, [useMobileAppShell]);

  // Lock document scroll on dashboard home — panels scroll internally only.
  useEffect(() => {
    if (!isDashboardViewportLock) return undefined;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [isDashboardViewportLock]);

  // Fetch hotels list
  useEffect(() => {
    const fetchHotels = async () => {
      if (hotelMenuAnchor) {
        setLoadingHotels(true);
        try {
          const hotelsData = await getHotels();
          setHotels(hotelsData);
        } catch (error) {
        } finally {
          setLoadingHotels(false);
        }
      }
    };
    fetchHotels();
  }, [hotelMenuAnchor]);

  useEffect(() => {
    const openMore = () => setMobileMoreOpen(true);
    window.addEventListener('roomnexa:mobile-more-open', openMore);
    return () => window.removeEventListener('roomnexa:mobile-more-open', openMore);
  }, []);

  // Dashboard menu items (Insights popover)
  const dashboardMenuItems = [
    {
      title: 'Performance',
      shortTitle: 'Performance',
      icon: 'mui-trending-up',
      url: '/dashboard/performance/',
      // Property Insights: organisation-dashboard permission or reports access.
      permissionCodes: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.REPORTS_VIEW],
      permissionCode: PERMISSIONS.DASHBOARD_VIEW,
      color: '#00897B',
      description: 'Hotel analytics for the selected property',
    },
    {
      title: 'Booking Dashboard',
      shortTitle: 'Bookings',
      icon: 'lnr-calendar-full',
      url: '/booking/dashboard/',
      // Insights analytics — not granted via booking ops alone (Front Office).
      permissionCodes: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.REPORTS_VIEW],
      permissionCode: PERMISSIONS.DASHBOARD_VIEW,
      color: '#2196F3',
      description: 'Reservations & Stays',
    },
    {
      title: 'Finance Dashboard',
      shortTitle: 'Finance',
      icon: 'lnr-store',
      url: '/finance/',
      permissionCode: PERMISSIONS.FINANCE_MANAGE,
      color: '#4CAF50',
      description: 'Revenue & Expenses',
    },
    {
      title: 'Inventory Dashboard',
      shortTitle: 'Inventory',
      icon: 'lnr-database',
      url: '/inventory/',
      permissionCode: PERMISSIONS.INVENTORY_MANAGE,
      color: '#FF9800',
      description: 'Inventory Management',
    },
    {
      title: 'Customer Dashboard',
      shortTitle: 'Customers',
      icon: 'lnr-users',
      url: '/customerhub/',
      permissionCode: PERMISSIONS.CUSTOMER_MANAGE,
      color: '#9C27B0',
      description: 'Guest Management',
    },
    {
      title: 'Report',
      shortTitle: 'Report',
      icon: 'mui-assessment',
      url: '/finance/reports/',
      permissionCode: PERMISSIONS.REPORTS_VIEW,
      color: '#607D8B',
      description: 'Reports, exports & police log',
    },
  ].filter((item) => {
    if (item.permissionCode === PERMISSIONS.REPORTS_VIEW) {
      return hasHotelFeature(PERMISSIONS.REPORTS_VIEW) || hasHotelFeature(PERMISSIONS.FINANCE_MANAGE);
    }
    const permissionCodes = item.permissionCodes || [item.permissionCode];
    return permissionCodes.some((permissionCode) => hasHotelFeature(permissionCode));
  }).map((item) => ({
    ...item,
    disabled: item.permissionCode === PERMISSIONS.REPORTS_VIEW
      ? !(canUse(PERMISSIONS.REPORTS_VIEW) || canUse(PERMISSIONS.FINANCE_MANAGE))
      : !(item.permissionCodes || [item.permissionCode]).some((permissionCode) => canUse(permissionCode)),
  }));

  const isInsightsMenuRouteActive = dashboardMenuItems.some((item) => {
    if (location.pathname === item.url) return true;
    if (item.url === '/finance/' && location.pathname === '/finance') return true;
    // Finance dashboard is only /finance/ — not /finance/expenses/, /finance/payments/, etc.
    if (item.url === '/finance/') return false;
    return location.pathname.startsWith(`${item.url}/`);
  });
  const isRestaurantRouteActive =
    location.pathname === '/restaurant/' ||
    location.pathname.startsWith('/restaurant/');
  const isHallRouteActive =
    location.pathname === '/hall/' ||
    location.pathname.startsWith('/hall/');
  const isInventoryDashboardRouteActive =
    location.pathname === '/stock/' ||
    location.pathname.startsWith('/stock/');

  const menuItems = [
    {
      title: 'Dashboard',
      shortTitle: 'Dashboard',
      icon: 'lnr-home',
      url: '/dashboard/',
      // Ops calendar: managers via dashboard permission; Front Office via booking ops.
      permissionCodes: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.BOOKING_OPERATIONS],
      permissionCode: PERMISSIONS.DASHBOARD_VIEW,
    },
    {
      title: 'Booking',
      shortTitle: 'Booking',
      icon: 'lnr-calendar-full',
      url: '/booking/',
      permissionCode: PERMISSIONS.BOOKING_OPERATIONS,
    },
  ].filter(() => hasHotelModule(HOTEL_MODULES.PMS)).map((item) => ({
    ...item,
    disabled: !(item.permissionCodes || [item.permissionCode]).some((permissionCode) => canUse(permissionCode)),
  }));

  const bookingItems = [
    { title: 'Reservation', shortTitle: 'Reservation', icon: 'lnr-calendar-full', url: '/booking/reservation/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'Walk In', shortTitle: 'Walk In', icon: 'lnr-user', url: '/booking/walk-in/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'Stay', shortTitle: 'Stay', icon: 'lnr-clock', url: '/booking/stay/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'Check In', shortTitle: 'Check In', icon: 'lnr-enter', url: '/booking/checkin/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'Check Out', shortTitle: 'Check Out', icon: 'lnr-exit', url: '/booking/checkout/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'Room Allocation', shortTitle: 'Allocation', icon: 'lnr-map', url: '/booking/room-allocation/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
    { title: 'No Show', shortTitle: 'No Show', icon: 'lnr-cross-circle', url: '/booking/no-show/', permissionCode: PERMISSIONS.BOOKING_OPERATIONS },
  ].filter(() => hasHotelModule(HOTEL_MODULES.PMS)).map((item) => ({
    ...item,
    disabled: !canUse(item.permissionCode),
  }));

  const financeItems = [];

  // Apps contains package-enabled management app options; department modules stay outside this popover.
  const settingsMenuItems = [
    { title: 'Manage Inventory', shortTitle: 'Daily Inventory', icon: 'lnr-database', url: '/inventory/manage/', permissionCode: PERMISSIONS.INVENTORY_MANAGE },
    { title: 'Manage Rate', shortTitle: 'Daily Rate', icon: 'lnr-rate', url: '/finance/rates/', permissionCode: PERMISSIONS.FINANCE_MANAGE },
    { title: 'Dynamic Pricing', shortTitle: 'Dynamic Pricing', icon: 'lnr-tag', url: '/dynamic-pricing/', permissionCode: PERMISSIONS.DYNAMIC_PRICING_MANAGE },
    { title: 'Distribution', shortTitle: 'Distribution', icon: 'lnr-layers', url: '/distribution/', permissionCodes: [PERMISSIONS.DISTRIBUTION_MANAGE, PERMISSIONS.BOOKING_ENGINE_MANAGE] },
    { title: 'Configuration', shortTitle: 'Configuration', icon: 'lnr-cog', url: '/configuration/', permissionCode: PERMISSIONS.CONFIGURATION_MANAGE },
    { title: 'POS', shortTitle: 'POS', icon: 'lnr-store', url: '/pos-restaurant/', permissionCode: PERMISSIONS.POS_RESTAURANT_MANAGE },
  ];
  const visibleSettingsMenuItems = settingsMenuItems.filter((item) => {
    const permissionCodes = item.permissionCodes || [item.permissionCode];
    return permissionCodes.some((permissionCode) => hasHotelFeature(permissionCode));
  }).map((item) => ({
    ...item,
    disabled: !(item.permissionCodes || [item.permissionCode]).some((permissionCode) => canUse(permissionCode)),
  }));
  // Visibility follows hotel package/add-ons; user role only disables entries.
  const hasAppsMenuItems = visibleSettingsMenuItems.length > 0;
  const hasInsightsMenuItems = dashboardMenuItems.length > 0;
  const hasEnabledAppsMenuItems = visibleSettingsMenuItems.some((item) => !item.disabled);
  const hasEnabledInsightsMenuItems = dashboardMenuItems.some((item) => !item.disabled);
  const showRestaurant = hasHotelFeature(PERMISSIONS.POS_RESTAURANT_MANAGE);
  const canAccessRestaurant = canUse(PERMISSIONS.POS_RESTAURANT_MANAGE);
  const showHall = hasHotelFeature(PERMISSIONS.HALL_MANAGE);
  const canAccessHall = canUse(PERMISSIONS.HALL_MANAGE);
  const showHousekeeping = hasHotelFeature(PERMISSIONS.HOUSEKEEPING_MANAGE);
  const canAccessHousekeeping = canUse(PERMISSIONS.HOUSEKEEPING_MANAGE);
  const showInventoryDashboard = hasHotelFeature(PERMISSIONS.STOCK_MANAGE);
  const canAccessInventoryDashboard = canUse(PERMISSIONS.STOCK_MANAGE);
  const isSettingsMenuRouteActive = visibleSettingsMenuItems.some((item) =>
    location.pathname === item.url || location.pathname.startsWith(item.url)
  );

  const otherItems = []; // Removed - now in topbar settings menu

  const isActive = (url) => {
    const path = optimisticPathname ?? location.pathname;
    if (url === '/dashboard/' || url === '/dashboard') {
      return path === '/dashboard/' || path === '/dashboard';
    }
    if (url === '/booking/' || url === '/booking') {
      return path === '/booking/' || path === '/booking';
    }
    if (path === url) return true;
    const prefix = url.endsWith('/') ? url : `${url}/`;
    return path.startsWith(prefix);
  };

  useEffect(() => {
    setOptimisticPathname(null);
  }, [location.pathname]);

  const scheduleDashboardWarm = useCallback(() => {
    queueMicrotask(() => {
      if (dashboardNeedsRefresh()) {
        invalidateDashboardHomeCaches();
      }
      scheduleDashboardPrefetch({
        forceRefresh: dashboardNeedsRefresh(),
      });
    });
  }, []);

  const handleNavClick = useCallback((url) => {
    const target = url === '/dashboard' ? '/dashboard/' : url;
    setOptimisticPathname(target);
    navigate(target);
    if (target === '/dashboard/' || target === '/dashboard') {
      scheduleDashboardWarm();
    }
  }, [navigate, scheduleDashboardWarm]);

  const blurFocusedMenuElement = useCallback(() => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return;
    const isInsideMenuOverlay = Boolean(
      activeElement.closest('.MuiMenu-paper, .MuiPopover-root, .MuiModal-root')
    );
    if (isInsideMenuOverlay) {
      activeElement.blur();
    }
  }, []);

  const closeAllTopMenus = useCallback(() => {
    pendingInsightsNavRef.current = null;
    pendingAppsNavRef.current = null;
    blurFocusedMenuElement();
    setProfileSheetOpen(false);
    setProfileMenuAnchor(null);
    setHotelMenuAnchor(null);
    setDashboardMenuAnchor(null);
    setSettingsMenuAnchor(null);
  }, [blurFocusedMenuElement]);

  useEffect(() => {
    const hasAnyTopMenuOpen =
      Boolean(profileMenuAnchor) ||
      profileSheetOpen ||
      Boolean(hotelMenuAnchor) ||
      Boolean(dashboardMenuAnchor) ||
      Boolean(settingsMenuAnchor);

    if (!hasAnyTopMenuOpen) return undefined;

    const handleDocumentPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      const anchors = [
        profileMenuAnchor,
        hotelMenuAnchor,
        dashboardMenuAnchor,
        settingsMenuAnchor,
      ].filter(Boolean);

      const clickedAnchor = anchors.some((anchor) => anchor && anchor.contains(target));
      if (clickedAnchor) return;

      if (target instanceof Element) {
        const insideMenuOverlay = Boolean(
          target.closest(
            '.MuiMenu-paper, [role="menu"], .MuiDrawer-paper, .MuiDialog-paper, .MuiPopover-paper'
          )
        );
        if (insideMenuOverlay) return;
      }

      closeAllTopMenus();
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
    };
  }, [
    profileMenuAnchor,
    profileSheetOpen,
    hotelMenuAnchor,
    dashboardMenuAnchor,
    settingsMenuAnchor,
    closeAllTopMenus,
  ]);

  // Close header menus on route change; see ROOMNEXA_MAINLAYOUT_DEFERRED_MENU_NAV (stale anchorEl → stuck overlay).
  useEffect(() => {
    closeAllTopMenus();
  }, [location.key, closeAllTopMenus]);

  useLayoutEffect(() => {
    const clearIfDetached = (el, setter) => {
      if (el && el instanceof Element && !document.body.contains(el)) {
        setter(null);
      }
    };
    clearIfDetached(profileMenuAnchor, setProfileMenuAnchor);
    clearIfDetached(hotelMenuAnchor, setHotelMenuAnchor);
    clearIfDetached(dashboardMenuAnchor, setDashboardMenuAnchor);
    clearIfDetached(settingsMenuAnchor, setSettingsMenuAnchor);
  }, [
    profileMenuAnchor,
    hotelMenuAnchor,
    dashboardMenuAnchor,
    settingsMenuAnchor,
  ]);

  const sharedMenuFocusProps = {
    autoFocus: false,
    disableAutoFocusItem: true,
    disableRestoreFocus: true,
    MenuListProps: {
      autoFocusItem: false,
    },
  };

  const handleProfileMenuOpen = (event) => {
    closeAllTopMenus();
    if (useMobileAppShell) {
      setProfileSheetOpen(true);
    } else {
      setProfileMenuAnchor(event.currentTarget);
    }
  };

  const handleProfileMenuClose = () => {
    blurFocusedMenuElement();
    setProfileSheetOpen(false);
    setProfileMenuAnchor(null);
  };

  const handleLockScreen = () => {
    handleProfileMenuClose();
    setScreenLocked(true);
  };

  const handleUnlockScreen = () => {
    setScreenLocked(false);
  };

  const handleHotelMenuOpen = (event) => {
    closeAllTopMenus();
    setHotelMenuAnchor(event.currentTarget);
  };

  const handleHotelMenuClose = () => {
    blurFocusedMenuElement();
    setHotelMenuAnchor(null);
  };

  const handleDashboardMenuOpen = (event) => {
    if (!hasInsightsMenuItems) return;
    closeAllTopMenus();
    setDashboardMenuAnchor(event.currentTarget);
  };

  const handleDashboardMenuClose = () => {
    blurFocusedMenuElement();
    setDashboardMenuAnchor(null);
  };

  const handleSettingsMenuOpen = (event) => {
    if (!hasAppsMenuItems) return;
    closeAllTopMenus();
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    blurFocusedMenuElement();
    setSettingsMenuAnchor(null);
  };

  const handleHotelSwitch = (hotelId) => {
    handleHotelMenuClose();
    // Switch hotel by redirecting with hotel_id parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('hotel_id', hotelId);
    window.location.href = currentUrl.toString();
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    // Logout is CSRF exempt, so we can submit without token
    // But include it if available for better security
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/logout/';
    
    // Include CSRF token if available, but don't block logout if missing
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfmiddlewaretoken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    
    document.body.appendChild(form);
    form.submit();
  };

  // Fixed width for icon + label sidebar (optimized for better spacing)
  const drawerWidth = 100;

  const wrapRoleDeniedTooltip = ({
    disabled,
    permissionCode,
    label,
    placement = 'bottom',
    children,
    tooltipKey,
    fullWidth = false,
  }) => {
    if (!disabled) return children;
    return (
      <Tooltip
        key={tooltipKey}
        title={getPermissionDeniedMessage(permissionCode, label)}
        placement={placement}
        arrow
      >
        {/* Fade/disable wrapper — keeps original tile geometry, softens content only */}
        <Box
          component="span"
          sx={{
            display: fullWidth ? 'block' : 'inline-flex',
            width: fullWidth ? '100%' : 'auto',
            maxWidth: '100%',
            position: 'relative',
            cursor: 'not-allowed',
            opacity: 0.42,
            filter: 'grayscale(0.45)',
            transition: 'opacity 0.2s ease, filter 0.2s ease',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: fullWidth ? 4 : 0,
              borderRadius: fullWidth ? '12px' : '8px',
              background: isDark
                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.38) 100%)'
                : 'linear-gradient(180deg, rgba(248, 250, 252, 0.2) 0%, rgba(241, 245, 249, 0.45) 100%)',
              pointerEvents: 'none',
              zIndex: 2,
            },
            '&:hover': {
              opacity: 0.5,
            },
          }}
        >
          {children}
        </Box>
      </Tooltip>
    );
  };

  // Render menu item helper - Material UI Creative Button Style
  const renderMenuItem = (item) => {
    const IconComponent = iconMap[item.icon] || HomeIcon;
    const selected = isActive(item.url);
    const shortTitle = item.shortTitle || item.title;
    const disabled = Boolean(item.disabled);
    const deniedPermissionCode = item.permissionCode || item.permissionCodes?.[0];
    const itemContent = (
      <ListItem
        key={item.url}
        disablePadding
        sx={{
          marginBottom: 0,
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          direction: 'ltr', // Ensure content direction is correct
        }}
      >
          <StyledListItemButton
            isDark={isDark}
            isSelected={selected}
          sidebarOpen={true}
          // Do not use MUI `disabled` — it applies .Mui-disabled opacity and breaks sidebar tile design.
          aria-disabled={disabled || undefined}
          onMouseEnter={() => {
            if (!disabled && item.url === '/dashboard/') scheduleDashboardWarm();
          }}
          onFocus={() => {
            if (!disabled && item.url === '/dashboard/') scheduleDashboardWarm();
          }}
          onClick={() => {
            if (disabled) return;
            handleNavClick(item.url);
          }}
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            '&:hover': disabled ? {
              transform: 'none',
              background: selected
                ? undefined
                : (isDark ? 'transparent' : 'rgba(0, 0, 0, 0.01)'),
              boxShadow: selected ? undefined : (isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 1px rgba(255, 255, 255, 0.9)'),
            } : undefined,
          }}
        >
          <StyledListItemIcon isSelected={selected}>
              <IconComponent />
            </StyledListItemIcon>
            <StyledListItemText
              isDark={isDark}
              isSelected={selected}
          >
            {shortTitle}
          </StyledListItemText>
          </StyledListItemButton>
      </ListItem>
    );

    return wrapRoleDeniedTooltip({
      disabled,
      permissionCode: deniedPermissionCode,
      label: shortTitle,
      placement: 'right',
      tooltipKey: item.url,
      fullWidth: true,
      children: itemContent,
    });
  };

  // Render menu section - Fancy Creative Design with uniform spacing
  const renderMenuSection = (title, items, SectionIcon = null) => {
    if (!items || items.length === 0) return null;
    
    return (
      <Box sx={{ mb: 0, direction: 'ltr' }}>
        <List sx={{ padding: 0, margin: 0 }}>
          {items.map((item) => {
            return (
              <React.Fragment key={item.url}>
                {renderMenuItem(item)}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box 
      className={`theme-${chromeThemeMode} theme${useMobileAppShell ? ' mobile-app-shell' : ''}`}
      sx={{ 
        display: 'flex',
        flexDirection: useMobileAppShell ? 'column' : undefined,
        ...(useMobileAppShell ? getMobileShellViewportSx() : { minHeight: '100vh', height: 'auto', maxHeight: 'none' }),
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        overflowY: useMobileAppShell ? 'hidden' : 'visible',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        transition: 'background-color 0.3s ease',
        ...(useMobileAppShell ? { position: 'relative', overflow: 'hidden', backgroundColor: 'transparent', background: 'none' } : {}),
      }}
    >
      <CssBaseline />

      {useMobileAppShell ? (
        <MobileShellGraphBackdrop />
      ) : null}

      {useMobileAppShell ? (
        <MobileAppHeader
          className="rn-dashboard-part"
          selectedHotel={selectedHotel}
          user={user}
          onHotelClick={handleHotelMenuOpen}
          onProfileClick={handleProfileMenuOpen}
          hotelPickerOpen={Boolean(hotelMenuAnchor)}
          style={{ '--dashboard-delay': '0ms' }}
        />
      ) : null}
      
      {/* AppBar - Header */}
      {!useMobileAppShell ? (
      <StyledAppBar
        position="fixed"
        elevation={0}
        isDark={isDark}
        isTopMenuOpen={isTopMenuOpen}
        sx={
          isTabletNative
            ? {
                pt: 'env(safe-area-inset-top)',
                height: `calc(${TOPBAR_HEIGHT}px + env(safe-area-inset-top))`,
              }
            : undefined
        }
      >
        <Toolbar 
          sx={{ 
            minHeight: '0 !important',
            height: TOPBAR_HEIGHT,
            maxHeight: TOPBAR_HEIGHT,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            overflowX: 'hidden',
            overflowY: 'hidden',
            '&.MuiToolbar-root': {
              minHeight: '0 !important',
              paddingLeft: '0',
              paddingRight: '0',
            },
            '&.css-1brtlba': {
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
            },
          }}
        >
          {isTopMenuOpen && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                pointerEvents: 'none',
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.42)' : 'rgba(0, 0, 0, 0.16)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
              }}
            />
          )}

          {/* Left section - Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', flex: '0 0 auto', ml: { xs: 1, md: 2 } }}>
            {/* Logo */}
            <Box
              component="img"
              src={getStaticFileUrl(isDark ? 'topbar/logo_ed_dark.png' : 'topbar/logo_ed.png')}
              alt="RoomNexa"
              onClick={() => handleNavClick('/dashboard/')}
              onMouseEnter={scheduleDashboardWarm}
              onFocus={scheduleDashboardWarm}
              sx={{
                width: 'auto',
                height: isTabletLayout ? '58px' : '50px',
                maxWidth: isTabletLayout ? '320px' : { xs: '200px', md: 'none' },
                mr: isTabletLayout ? 0 : 2,
                objectFit: 'contain',
                objectPosition: 'left center',
                cursor: 'pointer',
              }}
            />
            
            {/* Hotel Information - hidden on tablet/iPad (logo-only top bar) */}
            {selectedHotel && !isTabletLayout && (
              <Box sx={{ ml: 2, display: { xs: 'none', md: 'block' } }}>
                <Box
                  onClick={handleHotelMenuOpen}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDark ? '#38373f' : 'rgb(228.75, 234, 249.75)',
                    },
                  }}
                >
                  <HotelIcon
                    sx={{
                      fontSize: '20px',
                      color: '#4ce1b6',
                      mr: 1,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: isDark ? '#fff' : '#333',
                        lineHeight: 1.2,
                        mb: 0.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {selectedHotel.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: isDark ? '#999' : '#666',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {selectedHotel.city}, {selectedHotel.country}
                    </Typography>
                  </Box>
                  <ChevronDownIcon
                    sx={{
                      fontSize: '16px',
                      color: isDark ? '#999' : '#666',
                      ml: 1,
                      transition: 'transform 0.2s ease',
                      transform: hotelMenuAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Right section - Notifications, Theme, Profile */}
          <Box sx={{ 
            position: 'absolute',
            right: 0,
            display: 'flex',
            height: '100%',
            marginRight: { xs: '8px', md: '15px' },
            alignItems: 'center',
            flexShrink: 0,
            minWidth: 0,
            gap: { xs: 0.5, md: 1 },
          }}>
            {/* Search Autocomplete */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              mr: 2,
              width: { md: '300px', lg: '400px', xl: '500px' },
              maxWidth: '100%',
            }}>
              <Autocomplete
                freeSolo
                fullWidth
                options={searchResults}
                loading={searchLoading}
                onInputChange={(event, newInputValue) => {
                  setSearchQuery(newInputValue);
                }}
                onChange={(event, newValue) => {
                  const selectedBookingId = Number(newValue?.id || newValue?.booking_id);
                  if (Number.isInteger(selectedBookingId) && selectedBookingId > 0) {
                    navigate(`/booking/detail/${selectedBookingId}/`);
                    setSearchQuery('');
                    setSearchResults([]);
                  }
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.guest_name || 'Guest'} - ${option.code || ''}`;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Search bookings..."
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)', fontSize: '20px' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        height: '40px',
                        width: '100%',
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4ce1b6',
                          borderWidth: '1px',
                        },
                        '& input': {
                          color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                          fontSize: '0.875rem',
                          padding: '8px 12px',
                          width: '100%',
                        },
                        '& input::placeholder': {
                          color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                          opacity: 1,
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const optionBookingId = Number(option?.id || option?.booking_id);
                  const bookingUrl = Number.isInteger(optionBookingId) && optionBookingId > 0
                    ? `/booking/detail/${optionBookingId}/`
                    : '#';
                  
                  // Format dates
                  const formatDate = (dateString) => {
                    if (!dateString || dateString === '—') return '-';
                    try {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    } catch {
                      return dateString;
                    }
                  };

                  return (
                    <Box
                      component="li"
                      {...props}
                      onClick={() => {
                        if (Number.isInteger(optionBookingId) && optionBookingId > 0) {
                          navigate(bookingUrl);
                          setSearchQuery('');
                          setSearchResults([]);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        py: 1.5,
                        px: 2,
                        textAlign: 'left',
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {/* Guest Name and Booking ID */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#ddd' : '#212121',
                          }}
                        >
                          {option.guest_name || 'Guest'}
                        </Typography>
                        {option.code && (
                          <Chip
                            label={`ID: ${option.code}`}
                            size="small"
                            sx={{
                              backgroundColor: alpha('#4ce1b6', 0.1),
                              color: '#4ce1b6',
                              fontWeight: 500,
                              height: 22,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                      
                      {/* Check-in and Check-out Dates */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                        {option.check_in_date && option.check_in_date !== '—' && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#999' : '#757575',
                              fontSize: '0.75rem',
                            }}
                          >
                            Check-in: {formatDate(option.check_in_date)}
                          </Typography>
                        )}
                        {option.check_out_date && option.check_out_date !== '—' && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#999' : '#757575',
                              fontSize: '0.75rem',
                            }}
                          >
                            Check-out: {formatDate(option.check_out_date)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                PaperComponent={(props) => (
                  <Paper
                    {...props}
                    sx={{
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                      border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      mt: 1,
                      '& .MuiAutocomplete-listbox': {
                        textAlign: 'left',
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-listbox': {
                    textAlign: 'left',
                  },
                }}
                noOptionsText={searchQuery && searchQuery.trim().length >= 2 ? 'No bookings found' : 'Type to search...'}
              />
            </Box>

            {/* Apps menu (inventory, rates, dynamic pricing, etc.) */}
            {hasAppsMenuItems ? wrapRoleDeniedTooltip({
              disabled: !hasEnabledAppsMenuItems,
              label: 'Apps',
              children: (
            <Box
              onClick={handleSettingsMenuOpen}
                sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                borderRadius: '12px',
                cursor: 'pointer',
                opacity: hasEnabledAppsMenuItems ? 1 : 0.45,
                position: 'relative',
                zIndex: isSettingsMenuOpen ? 2 : 'auto',
                backgroundColor: hasEnabledAppsMenuItems && (settingsMenuAnchor || isSettingsMenuRouteActive)
                  ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)') 
                  : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: hasEnabledAppsMenuItems && (settingsMenuAnchor || isSettingsMenuRouteActive)
                  ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                  : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
                }}
              >
              <AppsIcon 
                sx={{
                  fontSize: '22px',
                  color: hasEnabledAppsMenuItems && (settingsMenuAnchor || isSettingsMenuRouteActive)
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  color: hasEnabledAppsMenuItems && (settingsMenuAnchor || isSettingsMenuRouteActive)
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Apps
              </Typography>
            </Box>
              ),
            }) : null}

            {/* Insights (dashboard menu — includes Performance, Report, sub-dashboards) */}
            {hasInsightsMenuItems && wrapRoleDeniedTooltip({
              disabled: !hasEnabledInsightsMenuItems,
              label: 'Insights',
              children: (
            <Box
              onClick={handleDashboardMenuOpen}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                borderRadius: '12px',
                cursor: 'pointer',
                opacity: hasEnabledInsightsMenuItems ? 1 : 0.45,
                position: 'relative',
                zIndex: isDashboardMenuOpen ? 2 : 'auto',
                backgroundColor:
                  hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                    ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)')
                    : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow:
                  hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                    ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                    : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <DashboardIcon
                sx={{
                  fontSize: '22px',
                  color:
                    hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                      ? '#4ce1b6'
                      : isDark
                        ? '#ddd'
                        : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  color:
                    hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                      ? '#4ce1b6'
                      : isDark
                        ? '#ddd'
                        : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Insights
              </Typography>
            </Box>
              ),
            })}

            {/* Restaurant */}
            {showRestaurant && wrapRoleDeniedTooltip({
              disabled: !canAccessRestaurant,
              permissionCode: PERMISSIONS.POS_RESTAURANT_MANAGE,
              label: 'Restaurant',
              children: (
            <Box
              onClick={() => {
                if (canAccessRestaurant) navigate('/restaurant/');
              }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                borderRadius: '12px',
                cursor: canAccessRestaurant ? 'pointer' : 'not-allowed',
                opacity: canAccessRestaurant ? 1 : 0.45,
                backgroundColor: isRestaurantRouteActive
                  ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)')
                  : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isRestaurantRouteActive
                  ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                  : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                  '& .MuiSvgIcon-root': { color: '#4ce1b6' },
                  '& .MuiTypography-root': { color: '#4ce1b6' },
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <RestaurantMenuIcon
                sx={{
                  fontSize: '22px',
                  color: isRestaurantRouteActive
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  transition: 'color 0.3s ease',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  color: isRestaurantRouteActive
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Restaurant
              </Typography>
            </Box>
              ),
            })}

            {/* Hall — hotel add-on entitlement; role only enables/disables */}
            {showHall && wrapRoleDeniedTooltip({
              disabled: !canAccessHall,
              permissionCode: PERMISSIONS.HALL_MANAGE,
              label: 'Hall',
              children: (
            <Box
              onClick={() => {
                if (canAccessHall) navigate('/hall/dashboard/');
              }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                borderRadius: '12px',
                cursor: canAccessHall ? 'pointer' : 'not-allowed',
                opacity: canAccessHall ? 1 : 0.45,
                backgroundColor: isHallRouteActive
                  ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)')
                  : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isHallRouteActive
                  ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                  : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                  '& .MuiSvgIcon-root': { color: '#4ce1b6' },
                  '& .MuiTypography-root': { color: '#4ce1b6' },
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <HallIcon
                sx={{
                  fontSize: '22px',
                  color: isHallRouteActive
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  transition: 'color 0.3s ease',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  color: isHallRouteActive
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Hall
              </Typography>
            </Box>
              ),
            })}

            {/* Housekeeping — extra horizontal padding + hover/active tint on icon + label */}
            {showHousekeeping && wrapRoleDeniedTooltip({
              disabled: !canAccessHousekeeping,
              permissionCode: PERMISSIONS.HOUSEKEEPING_MANAGE,
              label: 'Housekeeping',
              children: (
            <Box
              onClick={() => {
                if (canAccessHousekeeping) navigate('/housekeeping/');
              }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                minWidth: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                px: '4px',
                boxSizing: 'border-box',
                borderRadius: '12px',
                cursor: canAccessHousekeeping ? 'pointer' : 'not-allowed',
                opacity: canAccessHousekeeping ? 1 : 0.45,
                backgroundColor:
                  location.pathname === '/housekeeping/' || location.pathname.startsWith('/housekeeping/')
                    ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)')
                    : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow:
                  location.pathname === '/housekeeping/' || location.pathname.startsWith('/housekeeping/')
                    ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                    : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                  '& .MuiSvgIcon-root': { color: '#4ce1b6' },
                  '& .MuiTypography-root': { color: '#4ce1b6' },
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <CleaningServicesIcon
                sx={{
                  fontSize: '22px',
                  color:
                    location.pathname === '/housekeeping/' || location.pathname.startsWith('/housekeeping/')
                      ? '#4ce1b6'
                      : isDark
                        ? '#ddd'
                        : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  transition: 'color 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  color:
                    location.pathname === '/housekeeping/' || location.pathname.startsWith('/housekeeping/')
                      ? '#4ce1b6'
                      : isDark
                        ? '#ddd'
                        : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Housekeeping
              </Typography>
            </Box>
              ),
            })}

            {showInventoryDashboard && wrapRoleDeniedTooltip({
              disabled: !canAccessInventoryDashboard,
              permissionCode: PERMISSIONS.STOCK_MANAGE,
              label: 'Stock',
              children: (
            <Box
              onClick={() => {
                if (canAccessInventoryDashboard) navigate('/stock/');
              }}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: TOPBAR_NAV_TILE,
                height: TOPBAR_NAV_TILE,
                borderRadius: '12px',
                cursor: canAccessInventoryDashboard ? 'pointer' : 'not-allowed',
                opacity: canAccessInventoryDashboard ? 1 : 0.45,
                backgroundColor: isInventoryDashboardRouteActive
                  ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)')
                  : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isInventoryDashboardRouteActive
                  ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                  : 'none',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.25)'
                    : '0 4px 12px rgba(76, 225, 182, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <InventoryIcon
                sx={{
                  fontSize: '22px',
                  color: isInventoryDashboardRouteActive
                    ? '#4ce1b6'
                    : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: '12px',
                  color: isInventoryDashboardRouteActive
                    ? '#4ce1b6'
                    : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                  fontWeight: 500,
                  textAlign: 'center',
                  transition: 'color 0.3s ease',
                }}
              >
                Stock
              </Typography>
            </Box>
              ),
            })}

            <ThemeToggleButton compact={isTabletLayout || useMobileAppShell} sx={{ mx: 0.25 }} />

            {/* Profile */}
            <Tooltip title="Profile" arrow placement="bottom">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                width: 'auto',
                  height: '48px',
                  padding: '0 12px',
                  borderRadius: '12px',
                color: isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                  backgroundColor: (profileMenuAnchor || profileSheetOpen)
                    ? (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.12)') 
                    : 'transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: (profileMenuAnchor || profileSheetOpen)
                    ? (isDark ? '0 2px 8px rgba(76, 225, 182, 0.2)' : '0 2px 8px rgba(76, 225, 182, 0.15)')
                    : 'none',
                '&:hover': {
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark 
                      ? '0 4px 12px rgba(76, 225, 182, 0.25)' 
                      : '0 4px 12px rgba(76, 225, 182, 0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(0px) scale(0.95)',
                },
              }}
            >
              <Avatar
                sx={{
                    width: 32,
                    height: 32,
                  bgcolor: '#4ce1b6',
                  fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 4px rgba(76, 225, 182, 0.3)',
                }}
              >
                {user.username?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </StyledAppBar>
      ) : null}

      {/* Mobile Horizontal Menu Bar - Appears after header */}
      {isMobile && !useMobileAppShell && (
      <Box
        component="nav"
        sx={{
            position: 'fixed',
            top: TOPBAR_HEIGHT,
            left: 0,
            right: 0,
            height: '80px',
            background: isDark ? DARK_CHROME_GRADIENT : '#ffffff',
            backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
            borderBottom: `1px solid ${isDark ? DARK_CHROME_BORDER : '#e8eaed'}`,
            zIndex: 99,
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            py: 0.5,
            gap: 1,
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark 
                ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.4) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                : 'linear-gradient(90deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.15) 100%)',
              borderRadius: '10px',
              border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)'}`,
            },
          }}
        >
          {/* Insights (mobile sidebar) — show when hotel package includes it; role only disables */}
          {hasInsightsMenuItems && wrapRoleDeniedTooltip({
            disabled: !hasEnabledInsightsMenuItems,
            label: 'Insights',
            placement: 'top',
            children: (
          <Box
            onClick={(e) => {
              const event = {
                currentTarget: e.currentTarget,
              };
              handleDashboardMenuOpen(event);
            }}
            sx={{
              minWidth: '68px',
              width: '68px',
              height: '68px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              margin: 0,
              padding: '8px 4px',
              flexShrink: 0,
              position: 'relative',
              cursor: 'pointer',
              opacity: hasEnabledInsightsMenuItems ? 1 : 0.45,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor:
                hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                  ? (isDark ? 'rgba(255, 255, 255, 0.06)' : '#f5f5f5')
                  : 'transparent',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f5f5f5',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive) ? '60%' : '0px',
                height: '4px',
                background: 'linear-gradient(90deg, #4ce1b6 0%, rgba(76, 225, 182, 0.5) 100%)',
                borderRadius: '4px 4px 0 0',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
          >
            <DashboardIcon
              sx={{
                fontSize: 28,
                color:
                  hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : '#666',
                mb: 0.5,
                transition: 'color 0.3s ease',
              }}
            />
            <Typography
              sx={{
                fontSize: '10px',
                lineHeight: '12px',
                color:
                  hasEnabledInsightsMenuItems && (dashboardMenuAnchor || isInsightsMenuRouteActive)
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : '#666',
                fontWeight: 500,
                textAlign: 'center',
                transition: 'color 0.3s ease',
              }}
            >
              Insights
            </Typography>
          </Box>
            ),
          })}

          {/* Restaurant Button */}
          {showRestaurant && wrapRoleDeniedTooltip({
            disabled: !canAccessRestaurant,
            permissionCode: PERMISSIONS.POS_RESTAURANT_MANAGE,
            label: 'Restaurant',
            placement: 'top',
            children: (
          <Box
            onClick={() => {
              if (canAccessRestaurant) navigate('/restaurant/');
            }}
            sx={{
              minWidth: '68px',
              width: '68px',
              height: '68px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              margin: 0,
              padding: '8px 4px',
              flexShrink: 0,
              position: 'relative',
              cursor: canAccessRestaurant ? 'pointer' : 'not-allowed',
              opacity: canAccessRestaurant ? 1 : 0.45,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: isRestaurantRouteActive
                ? (isDark
                    ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.15) 0%, rgba(76, 225, 182, 0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.06) 100%)')
                : 'transparent',
              borderTop: isRestaurantRouteActive
                ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
                : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
              boxShadow: isRestaurantRouteActive
                ? (isDark
                    ? '0 4px 12px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
                    : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8)')
                : (isDark
                    ? '0 1px 3px rgba(0, 0, 0, 0.2)'
                    : '0 1px 3px rgba(0, 0, 0, 0.08)'),
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: isRestaurantRouteActive ? '60%' : '0%',
                height: isRestaurantRouteActive ? '4px' : '0px',
                background: 'linear-gradient(90deg, #4ce1b6 0%, rgba(76, 225, 182, 0.6) 100%)',
                borderRadius: '0 0 4px 4px',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isRestaurantRouteActive
                  ? (isDark
                      ? '0 6px 16px rgba(76, 225, 182, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.15)'
                      : '0 6px 16px rgba(76, 225, 182, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.9)')
                  : (isDark
                      ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                      : '0 4px 8px rgba(0, 0, 0, 0.12)'),
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                width: '28px',
                height: '28px',
                marginBottom: '4px',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: isRestaurantRouteActive ? '100%' : '0%',
                  height: isRestaurantRouteActive ? '100%' : '0%',
                  borderRadius: '50%',
                  background: isRestaurantRouteActive ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 0,
                },
              }}
            >
              <RestaurantMenuIcon
                sx={{
                  fontSize: 20,
                  color: isRestaurantRouteActive
                    ? '#4ce1b6'
                    : isDark
                      ? '#ddd'
                      : '#666',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: '10px',
                lineHeight: '12px',
                color: isRestaurantRouteActive
                  ? '#4ce1b6'
                  : isDark
                    ? '#ddd'
                    : '#666',
                fontWeight: 500,
                textAlign: 'center',
                transition: 'color 0.3s ease',
              }}
            >
              Restaurant
            </Typography>
          </Box>
            ),
          })}

          {/* All menu items in horizontal layout */}
          {[...menuItems, ...bookingItems, ...financeItems, ...otherItems].map((item) => {
            const IconComponent = iconMap[item.icon] || HomeIcon;
            const selected = isActive(item.url);
            const shortTitle = item.shortTitle || item.title;
            const disabled = Boolean(item.disabled);
            const deniedPermissionCode = item.permissionCode || item.permissionCodes?.[0];

            return wrapRoleDeniedTooltip({
              disabled,
              permissionCode: deniedPermissionCode,
              label: shortTitle,
              placement: 'top',
              tooltipKey: item.url,
              children: (
              <Box
                key={item.url}
                onMouseEnter={() => {
                  if (item.url === '/dashboard/') scheduleDashboardWarm();
                }}
                onFocus={() => {
                  if (item.url === '/dashboard/') scheduleDashboardWarm();
                }}
                onClick={() => {
                  if (!disabled) handleNavClick(item.url);
                }}
                sx={{
                  minWidth: '68px',
                  width: '68px',
                  height: '68px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  margin: 0,
                  padding: '8px 4px',
                  flexShrink: 0,
                  position: 'relative',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: selected
                    ? (isDark
                        ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.15) 0%, rgba(76, 225, 182, 0.08) 100%)'
                        : 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.06) 100%)')
                    : 'transparent',
                  borderTop: selected
                    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
                    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                  borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                  boxShadow: selected
                    ? (isDark
                        ? '0 4px 12px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
                        : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8)')
                    : (isDark
                        ? '0 1px 3px rgba(0, 0, 0, 0.2)'
                        : '0 1px 3px rgba(0, 0, 0, 0.08)'),
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: selected ? '60%' : '0%',
                    height: selected ? '4px' : '0px',
                    background: 'linear-gradient(90deg, #4ce1b6 0%, rgba(76, 225, 182, 0.6) 100%)',
                    borderRadius: '0 0 4px 4px',
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: selected
                      ? (isDark
                          ? '0 6px 16px rgba(76, 225, 182, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.15)'
                          : '0 6px 16px rgba(76, 225, 182, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.9)')
                      : (isDark
                          ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                          : '0 4px 8px rgba(0, 0, 0, 0.12)'),
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '28px',
                    width: '28px',
                    height: '28px',
                    marginBottom: '4px',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: selected ? '100%' : '0%',
                      height: selected ? '100%' : '0%',
                      borderRadius: '50%',
                      background: selected ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 0,
                    },
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: '24px',
                      width: '24px',
                      height: '24px',
                      color: selected ? '#4ce1b6' : '#999',
                      position: 'relative',
                      zIndex: 1,
                      filter: selected ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </Box>
                {/* Label */}
                <Typography
                  sx={{
                    fontSize: '9px',
                    lineHeight: '12px',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#4ce1b6' : (isDark ? '#999' : '#666'),
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                    textShadow: selected ? '0 1px 2px rgba(76, 225, 182, 0.2)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {shortTitle}
                </Typography>
              </Box>
              ),
            });
          })}
        </Box>
      )}

      {/* Desktop Vertical Sidebar Navigation — hidden in phone mobile shell */}
      <Box
        component="nav"
        sx={{
          width: useMobileAppShell ? 0 : { xs: 0, md: `${drawerWidth}px` },
          flexShrink: 0,
          transition: 'width 0.3s',
          display: useMobileAppShell ? 'none' : { xs: 'none', md: 'block' },
        }}
      >
        <StyledDrawer
          variant="permanent"
          open={true}
          isDark={isDark}
          sidebarOpen={true}
          isMobile={false}
          sx={{
            display: useMobileAppShell ? 'none' : { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: `${drawerWidth}px`,
              transition: 'width 0.3s',
            },
          }}
        >
          {/* Paper paddingTop clears fixed AppBar; no second spacer — menu starts directly under top bar */}
          {/* Scrollable Sidebar Content - Fancy Creative Design with Left Scrollbar */}
          <Box
        sx={{
              height: isTabletNative
                ? `calc(100vh - ${TOPBAR_HEIGHT}px - env(safe-area-inset-top))`
                : `calc(100vh - ${TOPBAR_HEIGHT}px)`,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              direction: 'rtl', // Reverse direction to move scrollbar to left
              '& > *': {
                direction: 'ltr', // Reset direction for content
              },
              '&::-webkit-scrollbar': {
                width: '6px',
                left: 0,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
                margin: '8px 0',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark 
                  ? 'linear-gradient(180deg, rgba(76, 225, 182, 0.4) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                  : 'linear-gradient(180deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.15) 100%)',
                borderRadius: '10px',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)'}`,
                '&:hover': {
                  background: isDark 
                    ? 'linear-gradient(180deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 100%)' 
                    : 'linear-gradient(180deg, rgba(76, 225, 182, 0.5) 0%, rgba(76, 225, 182, 0.3) 100%)',
                },
          },
        }}
      >
            {/* Main Menu Section */}
            {renderMenuSection('Overview', menuItems, HomeIcon)}
            
            {/* Booking Section */}
            {renderMenuSection('Bookings', bookingItems, CalendarIcon)}
            
            {/* Finance Section */}
            {renderMenuSection('Finance', financeItems, StoreIcon)}
            
            {/* Apps section (items live in topbar Apps menu) */}
            {renderMenuSection('Apps', otherItems, AppsIcon)}
          </Box>
        </StyledDrawer>
      </Box>


      {/* Hotel Selection — mobile bottom sheet / desktop menu */}
      {useMobileAppShell ? (
        <MobileHotelPickerSheet
          open={Boolean(hotelMenuAnchor)}
          onClose={handleHotelMenuClose}
          hotels={hotels}
          selectedHotel={selectedHotel}
          loadingHotels={loadingHotels}
          onSelectHotel={handleHotelSwitch}
        />
      ) : (
        <Menu
          anchorEl={hotelMenuAnchor}
          open={Boolean(hotelMenuAnchor)}
          onClose={handleHotelMenuClose}
          {...sharedMenuFocusProps}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            zIndex: 1600,
          }}
          PaperProps={{
            sx: {
              backgroundColor: isDark ? '#232329' : '#ffffff',
              boxShadow: isDark
                ? '0 18px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)'
                : '0 2px 15px 0 rgba(0, 0, 0, 0.15)',
              maxHeight: 500,
              borderRadius: '12px',
              marginTop: '8px',
              overflow: 'hidden',
              minWidth: 320,
              maxWidth: 400,
              border: isDark ? '1px solid rgba(255,255,255,0.08)' : undefined,
            },
          }}
          MenuListProps={{
            sx: {
              width: '100%',
              py: 0,
            },
          }}
        >
          <Box
            sx={{
              padding: '16px',
              borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: isDark ? '#ddd' : '#212121',
                mb: 0.5,
              }}
            >
              Select Hotel
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: isDark ? '#999' : '#757575',
              }}
            >
              Choose a hotel to switch context
            </Typography>
          </Box>
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: isDark ? '#2a2a31' : '#f5f5f5',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? '#555' : '#ccc',
                borderRadius: '3px',
                '&:hover': {
                  background: isDark ? '#666' : '#bbb',
                },
              },
            }}
          >
            {loadingHotels ? (
              <MenuItem
                disabled
                sx={{
                  padding: '12px 16px',
                  minHeight: 'auto',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderBottom: `1px solid ${isDark ? '#333246' : '#f0f0f0'}`,
                  opacity: '1 !important',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <HotelIcon
                    sx={{
                      fontSize: '24px',
                      color: isDark ? '#605f7b' : '#999',
                      mr: 2,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="66%" height={22} />
                    <Skeleton variant="text" width="44%" height={18} sx={{ mt: 0.25 }} />
                    <Skeleton variant="text" width={54} height={16} sx={{ mt: 0.25 }} />
                  </Box>
                  <Skeleton variant="circular" width={18} height={18} sx={{ ml: 1 }} />
                </Box>
              </MenuItem>
            ) : hotels.length === 0 ? (
              <Box sx={{ padding: '20px', textAlign: 'center' }}>
                <Typography sx={{ color: isDark ? '#999' : '#757575', fontSize: '14px' }}>
                  No hotels available
                </Typography>
              </Box>
            ) : (
              hotels.map((hotel) => (
                <MenuItem
                  key={hotel.id}
                  onClick={() => handleHotelSwitch(hotel.id)}
                  selected={selectedHotel && selectedHotel.id === hotel.id}
                  sx={{
                    padding: '12px 16px',
                    minHeight: 'auto',
                    width: '100%',
                    boxSizing: 'border-box',
                    borderBottom: `1px solid ${isDark ? '#333246' : '#f0f0f0'}`,
                    '&:hover': {
                      backgroundColor: isDark ? '#38373f' : 'rgb(228.75, 234, 249.75)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: isDark ? alpha('#4ce1b6', 0.14) : '#e8f5f0',
                      '&:hover': {
                        backgroundColor: isDark ? alpha('#4ce1b6', 0.18) : '#e8f5f0',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <HotelIcon
                      sx={{
                        fontSize: '24px',
                        color: selectedHotel && selectedHotel.id === hotel.id ? '#4ce1b6' : (isDark ? '#605f7b' : '#999'),
                        mr: 2,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: selectedHotel && selectedHotel.id === hotel.id ? 600 : 500,
                            color: isDark ? '#ddd' : '#212121',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {hotel.name}
                        </Typography>
                        {selectedHotel && selectedHotel.id === hotel.id && (
                          <CheckIcon
                            sx={{
                              fontSize: '18px',
                              color: '#4ce1b6',
                              ml: 1,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: isDark ? '#999' : '#757575',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {hotel.city && hotel.country
                          ? `${hotel.city}, ${hotel.country}`
                          : hotel.city || hotel.country || 'Location not specified'}
                      </Typography>
                      {hotel.star_rating_display && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            color: isDark ? '#666' : '#999',
                            mt: 0.25,
                          }}
                        >
                          {hotel.star_rating_display}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Menu>
      )}

      {/* Profile Menu — mobile bottom sheet / desktop menu */}
      {useMobileAppShell ? (
        <MobileProfileMenuSheet
          open={profileSheetOpen}
          onClose={handleProfileMenuClose}
          user={user}
          onMyProfile={() => {
            handleProfileMenuClose();
            navigate('/profile/');
          }}
          onSwitchHotel={() => {
            handleProfileMenuClose();
            window.location.href = '/onboarding/landing/?clear_hotel=true';
          }}
          onLockScreen={handleLockScreen}
          onLogout={handleLogout}
        />
      ) : (
        <ProfileDesktopMenu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
          isDark={isDark}
          user={user}
          onMyProfile={() => {
            handleProfileMenuClose();
            navigate('/profile/');
          }}
          onSwitchHotel={() => {
            handleProfileMenuClose();
            window.location.href = '/onboarding/landing/?clear_hotel=true';
          }}
          onLockScreen={handleLockScreen}
          onLogout={handleLogout}
          showSwitchHotel
        />
      )}

      {useMobileAppShell ? (
        <MobileLockScreenOverlay
          open={screenLocked}
          user={user}
          onUnlock={handleUnlockScreen}
        />
      ) : null}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: useMobileAppShell ? '100%' : { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: useMobileAppShell ? '100vw' : { xs: '100vw', md: `calc(100vw - ${drawerWidth}px)` },
          minHeight: useMobileAppShell ? 0 : (isDesktopDashboardHome ? 0 : '100vh'),
          ...(useMobileAppShell && isMobileDashboardHome
            ? { flex: '1 1 0', height: 0, minHeight: 0 }
            : {
              height: isDesktopDashboardHome ? '100vh' : undefined,
              maxHeight: isDesktopDashboardHome ? '100vh' : undefined,
            }),
          position: useMobileAppShell ? 'relative' : undefined,
          zIndex: useMobileAppShell ? 1 : undefined,
          ...(useMobileAppShell ? { backgroundColor: 'transparent' } : {
            backgroundColor: isDark ? '#12151c' : '#f2f4f7',
          }),
          paddingTop: useMobileAppShell
            ? `calc(${MOBILE_APP_HEADER_HEIGHT}px + env(safe-area-inset-top) + 4px)`
            : isTabletNative
              ? `calc(${TOPBAR_HEIGHT}px + env(safe-area-inset-top))`
              : { xs: `${TOPBAR_HEIGHT + 80}px`, md: `${TOPBAR_HEIGHT}px` },
          paddingBottom: useMobileAppShell
            ? MOBILE_LAYOUT.mobileMainBottomInset
            : 0,
          scrollPaddingTop: useMobileAppShell
            ? `calc(${MOBILE_APP_HEADER_HEIGHT}px + env(safe-area-inset-top) + 20px)`
            : undefined,
          scrollPaddingBottom: useMobileAppShell ? MOBILE_LAYOUT.mobileMainBottomInset : undefined,
          overscrollBehavior: useMobileAppShell ? 'none' : undefined,
          transition: 'width 0.3s, background-color 0.3s ease, padding-top 0.3s',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: useMobileAppShell
            ? (isMobileDashboardHome ? 'hidden' : 'auto')
            : (isDashboardViewportLock ? 'hidden' : 'visible'),
          WebkitOverflowScrolling: 'touch',
          ...(useMobileAppShell ? mobileHideScrollbarSx : {}),
          boxSizing: 'border-box',
        }}
      >
        <Box
          className={useMobileAppShell ? 'mobile-route-surface' : undefined}
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            minHeight: 0,
            position: 'relative',
            zIndex: 1,
            ...(isDashboardViewportLock ? { overflow: 'hidden', flex: '1 1 0', minHeight: 0, height: 0 } : {}),
            ...(useMobileAppShell ? mobileHideScrollbarSx : {}),
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Blur Backdrop for Apps and Insights menus - z-index below Menu (1500) so menu stays clickable on all browsers/OS */}
      <Backdrop
        open={
          (hasInsightsMenuItems && Boolean(dashboardMenuAnchor))
          || (hasAppsMenuItems && Boolean(settingsMenuAnchor))
        }
        onClick={() => {
          if (dashboardMenuAnchor) handleDashboardMenuClose();
          if (settingsMenuAnchor) handleSettingsMenuClose();
        }}
        sx={{
          zIndex: 1200,
          backgroundColor: isDark 
            ? 'rgba(0, 0, 0, 0.5)' 
            : 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {/* Dashboard Menu Popover */}
      <Menu
        anchorEl={dashboardMenuAnchor}
        open={hasInsightsMenuItems && Boolean(dashboardMenuAnchor)}
        onClose={handleDashboardMenuClose}
        hideBackdrop
        {...sharedMenuFocusProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        TransitionComponent={Fade}
        TransitionProps={{
          timeout: 200,
          onExited: () => {
            const url = pendingInsightsNavRef.current;
            pendingInsightsNavRef.current = null;
            if (url) navigate(url);
          },
        }}
        sx={{
          zIndex: 1500,
          '& .MuiPaper-root': getTopBarGridMenuPaperSx(isDark),
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            width: 'auto',
            minWidth: 'auto',
            maxWidth: 'none',
            p: 1,
            mt: 0.5,
          },
        }}
      >
        {/* Dashboard Grid — 3 columns so 6 tiles fit in 2 rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '4px',
            width: 'fit-content',
          }}
        >
          {dashboardMenuItems.map((item, index) => {
            const IconComponent = iconMap[item.icon] || HomeIcon;
            const disabled = Boolean(item.disabled);
            const tileActive =
              location.pathname === item.url ||
              (item.url === '/finance/' && location.pathname === '/finance') ||
              (item.url !== '/finance/' && location.pathname.startsWith(`${item.url}/`));
            
            return (
              <Tooltip
                key={item.url}
                title={disabled ? getPermissionDeniedMessage(item.permissionCode, item.shortTitle || item.title) : ''}
                placement="top"
                arrow
                disableHoverListener={!disabled}
                disableFocusListener={!disabled}
                disableTouchListener={!disabled}
              >
                <Box>
                <Box
                  onClick={() => {
                    if (disabled) return;
                    pendingInsightsNavRef.current = item.url;
                    handleDashboardMenuClose();
                  }}
                  sx={{
                    height: '90px',
                    width: '90px',
                    margin: '3px',
                    padding: '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderRadius: '14px',
                    border: tileActive
                      ? '2px solid #4ce1b6'
                      : `1px solid ${isDark ? DARK_CHROME_BORDER : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: tileActive
                      ? (isDark 
                          ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.25) 0%, rgba(76, 225, 182, 0.15) 100%)' 
                          : 'linear-gradient(135deg, rgba(76, 225, 182, 0.22) 0%, rgba(76, 225, 182, 0.12) 100%)')
                      : (isDark
                          ? 'transparent'
                          : 'rgba(0, 0, 0, 0.02)'),
                    boxShadow: tileActive
                      ? (isDark 
                          ? '0 4px 16px rgba(76, 225, 182, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.15), 0 0 0 1px rgba(76, 225, 182, 0.2)' 
                          : '0 4px 16px rgba(76, 225, 182, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.9), 0 0 0 1px rgba(76, 225, 182, 0.15)')
                      : (isDark 
                          ? 'none' 
                          : '0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.95)'),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: tileActive
                        ? 'radial-gradient(circle at 30% center, rgba(76, 225, 182, 0.15) 0%, transparent 70%)'
                        : 'transparent',
                      opacity: tileActive ? 1 : 0,
                      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      pointerEvents: 'none',
                      borderRadius: '14px',
                    },
                    '&:hover': {
                      backgroundColor: tileActive
                        ? (isDark 
                            ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                            : 'linear-gradient(135deg, rgba(76, 225, 182, 0.27) 0%, rgba(76, 225, 182, 0.17) 100%)')
                        : (isDark 
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)' 
                            : 'linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, rgba(76, 225, 182, 0.04) 100%)'),
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: tileActive
                        ? (isDark 
                            ? '0 8px 24px rgba(76, 225, 182, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2), 0 0 0 1px rgba(76, 225, 182, 0.25)' 
                            : '0 8px 24px rgba(76, 225, 182, 0.35), inset 0 1px 2px rgba(255, 255, 255, 0.95), 0 0 0 1px rgba(76, 225, 182, 0.2)')
                        : (isDark 
                            ? '0 4px 12px rgba(0, 0, 0, 0.35), inset 0 1px 1px rgba(255, 255, 255, 0.05)' 
                            : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.98)'),
                      borderColor: tileActive
                        ? '#4ce1b6'
                        : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)'),
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(0.98)',
                      boxShadow: tileActive
                        ? (isDark
                            ? '0 2px 8px rgba(76, 225, 182, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)'
                            : '0 2px 8px rgba(76, 225, 182, 0.25), inset 0 2px 4px rgba(0, 0, 0, 0.1)')
                        : (isDark
                            ? 'inset 0 3px 6px rgba(0, 0, 0, 0.3)'
                            : 'inset 0 3px 6px rgba(0, 0, 0, 0.12)'),
                    },
                    '&::before': tileActive ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '70px',
                      width: '4px',
                      background: 'linear-gradient(180deg, rgba(76, 225, 182, 1) 0%, rgba(76, 225, 182, 0.8) 100%)',
                      borderRadius: '0 3px 3px 0',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 0 12px rgba(76, 225, 182, 0.6), inset -1px 0 2px rgba(255, 255, 255, 0.2)',
                      zIndex: 1,
                    } : {},
                  }}
                >
                  {/* Icon - Increased Size */}
                  <Box
                    sx={{
                      minWidth: '38px',
                      width: '38px',
                      height: '38px',
                      margin: 0,
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: tileActive ? '100%' : '0%',
                        height: tileActive ? '100%' : '0%',
                        borderRadius: '50%',
                        background: tileActive ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 0,
                      },
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 28,
                        width: 28,
                        height: 28,
                        color: tileActive
                          ? '#4ce1b6' 
                          : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                        transition: 'color 0.3s ease',
                        position: 'relative',
                        zIndex: 1,
                        filter: tileActive ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
                      }}
                    />
                  </Box>

                  {/* Title - Increased Size */}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '14px',
                      maxWidth: '92px',
                      textAlign: 'center',
                      color: tileActive
                        ? '#4ce1b6' 
                        : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                      fontWeight: tileActive ? 600 : 500,
                      transition: 'color 0.3s ease',
                      position: 'relative',
                      zIndex: 2,
                      letterSpacing: '0.2px',
                    }}
                  >
                    {item.shortTitle}
                  </Typography>
                </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Menu>

      {/* Apps menu popover */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={hasAppsMenuItems && Boolean(settingsMenuAnchor)}
        onClose={handleSettingsMenuClose}
        hideBackdrop
        {...sharedMenuFocusProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        TransitionComponent={Fade}
        TransitionProps={{
          timeout: 200,
          onExited: () => {
            const url = pendingAppsNavRef.current;
            pendingAppsNavRef.current = null;
            if (url) navigate(url);
          },
        }}
        sx={{
          zIndex: 1500,
          '& .MuiPaper-root': getTopBarGridMenuPaperSx(isDark),
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            width: 'auto',
            minWidth: 'auto',
            maxWidth: 'none',
            p: 1,
            mt: 0.5,
          },
        }}
      >
        {/* Apps grid - square items */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '4px',
            width: 'fit-content',
          }}
        >
          {visibleSettingsMenuItems.map((item, index) => {
            const IconComponent = iconMap[item.icon] || SettingsIcon;
            const isActive = location.pathname === item.url || location.pathname.startsWith(item.url + '/');
            const disabled = Boolean(item.disabled);
            const deniedPermissionCode = item.permissionCode || item.permissionCodes?.[0];
            
            return (
              <Tooltip
                key={item.url}
                title={disabled ? getPermissionDeniedMessage(deniedPermissionCode, item.shortTitle || item.title) : ''}
                placement="top"
                arrow
                disableHoverListener={!disabled}
                disableFocusListener={!disabled}
                disableTouchListener={!disabled}
              >
                <Box>
                <Box
                  onClick={() => {
                    if (disabled) return;
                    pendingAppsNavRef.current = item.url;
                    handleSettingsMenuClose();
                  }}
                  sx={{
                    height: '90px',
                    width: '90px',
                    margin: '3px',
                    padding: '12px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    borderRadius: '14px',
                    border: isActive
                      ? `2px solid #4ce1b6`
                      : `1px solid ${isDark ? DARK_CHROME_BORDER : 'rgba(0, 0, 0, 0.1)'}`,
                    backgroundColor: isActive
                      ? (isDark 
                          ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.25) 0%, rgba(76, 225, 182, 0.15) 100%)' 
                          : 'linear-gradient(135deg, rgba(76, 225, 182, 0.22) 0%, rgba(76, 225, 182, 0.12) 100%)')
                      : (isDark
                          ? 'transparent'
                          : 'rgba(0, 0, 0, 0.02)'),
                    boxShadow: isActive
                      ? (isDark 
                          ? '0 4px 16px rgba(76, 225, 182, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.15), 0 0 0 1px rgba(76, 225, 182, 0.2)' 
                          : '0 4px 16px rgba(76, 225, 182, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.9), 0 0 0 1px rgba(76, 225, 182, 0.15)')
                      : (isDark 
                          ? 'none' 
                          : '0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.95)'),
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: isActive
                        ? 'radial-gradient(circle at 30% center, rgba(76, 225, 182, 0.15) 0%, transparent 70%)'
                        : 'transparent',
                      opacity: isActive ? 1 : 0,
                      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      pointerEvents: 'none',
                      borderRadius: '14px',
                    },
                    '&:hover': {
                      backgroundColor: isActive
                        ? (isDark 
                            ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                            : 'linear-gradient(135deg, rgba(76, 225, 182, 0.27) 0%, rgba(76, 225, 182, 0.17) 100%)')
                        : (isDark 
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)' 
                            : 'linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, rgba(76, 225, 182, 0.04) 100%)'),
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: isActive
                        ? (isDark 
                            ? '0 8px 24px rgba(76, 225, 182, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2), 0 0 0 1px rgba(76, 225, 182, 0.25)' 
                            : '0 8px 24px rgba(76, 225, 182, 0.35), inset 0 1px 2px rgba(255, 255, 255, 0.95), 0 0 0 1px rgba(76, 225, 182, 0.2)')
                        : (isDark 
                            ? '0 4px 12px rgba(0, 0, 0, 0.35), inset 0 1px 1px rgba(255, 255, 255, 0.05)' 
                            : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.98)'),
                      borderColor: isActive
                        ? '#4ce1b6'
                        : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)'),
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(0.98)',
                      boxShadow: isActive
                        ? (isDark 
                            ? '0 2px 8px rgba(76, 225, 182, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)' 
                            : '0 2px 8px rgba(76, 225, 182, 0.25), inset 0 2px 4px rgba(0, 0, 0, 0.1)')
                        : (isDark 
                            ? 'inset 0 3px 6px rgba(0, 0, 0, 0.3)' 
                            : 'inset 0 3px 6px rgba(0, 0, 0, 0.12)'),
                    },
                    '&::before': isActive ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '70px',
                      width: '4px',
                      background: 'linear-gradient(180deg, rgba(76, 225, 182, 1) 0%, rgba(76, 225, 182, 0.8) 100%)',
                      borderRadius: '0 3px 3px 0',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 0 12px rgba(76, 225, 182, 0.6), inset -1px 0 2px rgba(255, 255, 255, 0.2)',
                      zIndex: 1,
                    } : {},
                  }}
                >
                  {/* Icon - Improved Design */}
                  <Box
                    sx={{
                      minWidth: '38px',
                      width: '38px',
                      height: '38px',
                      margin: 0,
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        width: isActive ? '100%' : '0%',
                        height: isActive ? '100%' : '0%',
                        borderRadius: '50%',
                        background: isActive ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 0,
                      },
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 28,
                        width: 28,
                        height: 28,
                        color: isActive 
                          ? '#4ce1b6' 
                          : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        zIndex: 1,
                        filter: isActive ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
                      }}
                    />
                  </Box>

                  {/* Title - Improved Design */}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '14px',
                      maxWidth: '100%',
                      textAlign: 'center',
                      color: isActive 
                        ? '#4ce1b6' 
                        : (isDark ? '#ddd' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
                      fontWeight: isActive ? 600 : 500,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      zIndex: 2,
                      letterSpacing: '0.2px',
                    }}
                  >
                    {item.shortTitle}
                  </Typography>
                </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Menu>

      {useMobileAppShell ? (
        <>
          {!keyboardOpen ? (
            <MobileBottomNav
              className="rn-dashboard-part"
              leftTabs={mobileLeftTabs}
              rightTabs={mobileRightTabs}
              activePath={`${location.pathname}${location.search}`}
              onNavigate={(url) => navigate(url)}
              onFabClick={() => navigate(mobileFabAction.url)}
              onMoreClick={() => setMobileMoreOpen(true)}
              style={{ '--dashboard-delay': '320ms' }}
            />
          ) : null}
          <MobileMoreDrawer
            open={mobileMoreOpen}
            onClose={() => setMobileMoreOpen(false)}
            groups={mobileNavGroups}
            onNavigate={(url) => navigate(url)}
          />
        </>
      ) : null}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialogOpen}
        onClose={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Booking Notifications */}
      {selectedHotel && (
        <BookingNotificationsWrapper hotelId={selectedHotel.id} />
      )}
      {selectedHotel && canAccessRestaurant && (
        <RestaurantQrOrderNotificationsWrapper hotelId={selectedHotel.id} />
      )}
    </Box>
  );
};

// Separate component for booking notifications to avoid re-renders
const BookingNotificationsWrapper = ({ hotelId }) => {
  const { notifications, removeNotification } = useBookingNotifications({
    hotelId: hotelId,
    pollInterval: 10000, // Poll every 10 seconds
    maxNotifications: 5,
    enabled: true,
  });

  return (
    <BookingNotificationContainer
      notifications={notifications}
      onRemove={removeNotification}
    />
  );
};

const RestaurantQrOrderNotificationsWrapper = ({ hotelId }) => {
  const navigate = useNavigate();
  const {
    dialogState,
    prepMinutes,
    setPrepMinutes,
    acceptOrder,
    declineOrder,
    closeDialog,
    actionError,
    pendingCount,
  } = useRestaurantQrOrderNotifications({
    hotelId,
    enabled: true,
  });

  return (
    <RestaurantQrOrderNotificationDialog
      open={dialogState.open}
      order={dialogState.activeOrder}
      queueCount={pendingCount}
      prepMinutes={prepMinutes}
      processing={dialogState.processing}
      actionError={actionError}
      onPrepMinutesChange={setPrepMinutes}
      onAccept={acceptOrder}
      onDecline={declineOrder}
      onClose={closeDialog}
      onOpenRestaurant={() => navigate('/restaurant/')}
    />
  );
};

export default MainLayout;